import { DUE_RANK } from '../types/models';
import type { ClientWithTasks, Task } from '../types/models';

export function activeTasks(client: ClientWithTasks): Task[] {
  return client.tasks.filter((t) => !t.done);
}

export function sortByDue(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => DUE_RANK[a.due] - DUE_RANK[b.due]);
}

export function clientMeta(client: ClientWithTasks): string {
  const open = activeTasks(client).length;
  const projects = client.projects.length;
  return `${open} OPEN · ${projects} PROJECT${projects === 1 ? '' : 'S'}`;
}

/** Sum free-form estimates ("1h", "45m") into a compact label. */
export function estTotalLabel(tasks: Task[]): string {
  let hours = 0;
  for (const t of tasks) {
    const h = /([\d.]+)\s*h/.exec(t.est);
    const m = /([\d.]+)\s*m/.exec(t.est);
    if (h) hours += parseFloat(h[1]);
    if (m) hours += parseFloat(m[1]) / 60;
  }
  if (hours >= 1) return `${Math.round(hours * 10) / 10}h`;
  if (hours > 0) return `${Math.round(hours * 60)}m`;
  return '0h';
}

export function projectName(client: ClientWithTasks, projectId: string): string {
  return client.projects.find((p) => p.id === projectId)?.name ?? '';
}

/** Count of links + attachments on a task (for the compact meta chip). */
export function refCount(task: Task): number {
  return (task.linkItems?.length ?? 0) + (task.attachments?.length ?? 0);
}

export function totalOpen(clients: ClientWithTasks[]): number {
  return clients.reduce((n, c) => n + activeTasks(c).length, 0);
}

/** A Lineup entry pairs a task with its owning client for cross-client display. */
export interface LineupEntry {
  client: ClientWithTasks;
  task: Task;
}

/** Flatten the current Lineup across clients, ordered by lineupOrder. */
export function lineupItems(clients: ClientWithTasks[]): LineupEntry[] {
  const entries: LineupEntry[] = [];
  for (const client of clients) {
    for (const task of client.tasks) {
      if (task.inLineup) entries.push({ client, task });
    }
  }
  return entries.sort(
    (a, b) => (a.task.lineupOrder ?? 0) - (b.task.lineupOrder ?? 0),
  );
}
